import ProjectData from '@/data/ProjectData.ts'

export const allProjects = [
    // ===== FormulaXR =====
    new ProjectData("xrproj", "XRProj", "img/projects/xrproj.png",
    `
    <div class="paragraph">
        <strong>XRProj</strong> is a <strong>codeless authoring platform</strong> for real-time 3D. Engineers import their own models, CAD or not, build animations and step-by-step sequences for quality checklists, training, and design review, then publish from one codebase to Quest, iOS, Android, PC (VR), HoloLens, and the web. Published on the <strong>Meta Quest Store, Apple App Store, and Google Play</strong>, in production since 2019.
    </div>
    <div class="paragraph">
        I've been the Lead Developer and Architect from day one, with the project under long-running continuous development. Used in production by <strong>Embraer</strong> (aircraft assembly review on Quest), and by <strong>Nestl&eacute;, Ball Corporation, and Aegea</strong> for collaborative 3D review and assembly validation. Distribution is enterprise/B2B rather than consumer, but the apps have been live on every major mobile and VR store since 2019.
    </div>
    <div class="paragraph">
        Engineers with no programming background author the procedures themselves. At one client, they documented over 24 assemblies and cut an inspection checklist from 2 hours to 1 hour. Client training time went from 5 hours to 3 hours. A client has also authored over 50 procedures and published them all through the web export. The CAD pipeline handles assemblies over 1 GB.
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/DJRpeYrGH80" frameborder="0" allowfullscreen></iframe>
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/sIcZepSxKfo" frameborder="0" allowfullscreen></iframe>
    </div>
    <div class="paragraph">
        Key contributions:
        <ul>
            <li>Led the architecture from concept to production</li>
            <li>The web target is a hand-built <strong>PlayCanvas/TypeScript</strong> port of the Unity client. Component-by-component, with a custom serialization layer that mirrors the Unity runtime. Written before LLMs existed; the mirroring was the slow part</li>
            <li>Real-time multiplayer, originally on Photon PUN, later migrated to a custom FishNet layer with <strong>Roslyn source generators and Mono.Cecil IL post-processing</strong> that preserves SyncVar/RPC/ownership semantics (shared with Virtua)</li>
            <li>Performance optimization for Quest (stereo, 72&ndash;90 Hz) and high-end mobile (iOS, Android)</li>
            <li><strong>Codeless authoring</strong>: engineers import their own models (CAD or polygon), then build animations and step-by-step sequences for quality checklists, training, and design review without writing code</li>
            <li>One authored project publishes to Android, iOS, WebGL, and desktop, with AR (XRTracker integrated) and VR optional per target</li>
            <li>Native CAD-to-runtime model conversion pipeline (CAD and polygon formats)</li>
            <li>Backend services in ASP.NET Core</li>
            <li>Led cross-functional teams across the full lifecycle</li>
        </ul>
    </div>
    <div class="paragraph">
        <div class="notice">
            Learn more at <a href="https://www.xrproj.com" target="_blank">xrproj.com</a>
        </div>
    </div>
    `, "#4DB6AC", "formulaxr", ["tools-sdks", "vr"]),

    new ProjectData("xrtracker", "XRTracker", "img/projects/xrtracker.png",
    `
    <div class="paragraph">
        <strong>XRTracker</strong> is a commercial Unity SDK for markerless 3D object tracking. 6DOF pose estimation with sub-centimeter precision. No scanning, markers, or cloud training required. Other developers drop it into their own Unity projects.
    </div>
    <div class="paragraph">
        Built ground-up with a <strong>native C++ computer vision core and GPU-accelerated rendering</strong>. Runs at <strong>4&ndash;8 ms per frame on mobile</strong>. Three tracking algorithms (silhouette, edge, depth) can be composed for robustness in low-light and low-contrast environments.
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/lMmO5UwQI-o" frameborder="0" allowfullscreen></iframe>
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/SDSiPCIFLNc" frameborder="0" allowfullscreen></iframe>
    </div>
    <div class="paragraph">
        Key features:
        <ul>
            <li>Native C++ runtime + GPU pipelines, integrated as a Unity SDK</li>
            <li>Three composable tracking algorithms (silhouette, edge, depth)</li>
            <li>In-editor authoring tooling: tracking model generation from any 3D mesh (FBX, OBJ, glTF) directly in the editor, with no-code setup through the Inspector</li>
            <li>Cross-platform: Windows, iOS (ARKit), Android (ARCore)</li>
            <li>Designed for Industry 4.0: Poka-Yoke workflows, assembly guidance, and task validation</li>
        </ul>
    </div>
    <div class="paragraph">
        <div class="notice">
            Visit <a href="https://xrtracker.net" target="_blank">xrtracker.net</a>, read the <a href="https://docs.xrtracker.net" target="_blank">documentation</a>, or browse the <a href="https://github.com/FormulaXR/xrtracker-package" target="_blank">public, versioned Unity package on GitHub</a>
        </div>
    </div>
    `, "#26A69A", "formulaxr", ["tools-sdks"]),

    new ProjectData("threejs-editor", "Three.js Editor - Web 3D Engine", "img/projects/threejs-editor.jpg",
    `
    <div class="paragraph">
        <strong>Three.js Editor</strong> is an internal engine and visual editor I'm building on top of <strong>three.js and React Three Fiber</strong>. It gives FormulaXR a web-native authoring tool with the systems you'd expect from a game engine: a scene graph with full undo/redo, prefabs with variants and overrides, a particle system, a shader graph, an animation timeline, and cameras, UI, and physics. It powers FormulaXR's web products and is not sold or open-sourced.
    </div>
    <div class="paragraph">
        Everything is defined by a versioned <strong>JSON schema</strong>. A scene can be authored visually in the editor or written directly by an AI against that same schema, because the format is identical either way. If something can't be serialized, it doesn't get built.
    </div>
    <div class="paragraph">
        I work <strong>spec-driven</strong>: every feature starts as a versioned schema, a written contract that comes before any implementation. A <strong>harness</strong> of rules, reusable skills, and a feature loop with automated gates sits on top of that. Type checks, regression tests, and a headless browser that drives the editor and screenshots it all run before any change reaches me. The model does the heavy coding within those guidelines, while I write the guidelines and review the architecture. The editor is in production, powering FormulaXR's education simulations.
    </div>
    <div class="paragraph">
        Over a dozen simulations are now in production on it. A new one starts from a scaffold project that already carries more than 20 authoring skills, so an agent can bootstrap a scene, author prefabs, animations and shader graphs, import assets, localize, and export. Two full simulations have shipped in a single week.
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/1iSKBVyhH2s" frameborder="0" allowfullscreen></iframe>
    </div>
    <div class="paragraph">
        Key features:
        <ul>
            <li>Scene graph with full undo/redo</li>
            <li>Prefabs with variants and overrides</li>
            <li>Particle system powered by <strong>three.quarks</strong></li>
            <li>Node-based shader graph</li>
            <li>Animation timeline and sequencing</li>
            <li>Cameras, UI, and a physics layer</li>
            <li>Light by design: a working project is about 60 MB, a shipped simulation is under 11 MB, testing on a device means opening a URL, and a release is a 30 to 60 second packaging step</li>
            <li>Single <strong>JSON schema</strong>: the same format for visual editing and AI authoring</li>
            <li>More than 20 authoring skills on top of the schema (scene bootstrap, prefabs, animation, shader graphs, localization, export), plus a scaffold project to start each simulation from</li>
            <li>Built spec-driven, with an automated harness (type checks, regression tests, headless-browser screenshots) that verifies the work before I review it</li>
        </ul>
    </div>
    `, "#3949AB", "formulaxr", ["tools-sdks"]),

    new ProjectData("virtua", "Virtua - Interactive Medical Simulation", "img/projects/virtua.png",
    `
    <div class="paragraph">
        <strong>Virtua</strong> is an interactive VR medical simulation developed for <strong>Becton Dickinson (BD)</strong>. Nurses put on a headset and practice central venous catheter insertion in VR before doing it on a real patient. <strong>Published on the Meta Quest Store</strong>, in production since 2021 across <strong>Brazil, Mexico, Colombia, Argentina, and Chile</strong>. Distribution is B2B rather than consumer, but the app has been live on the store and in active use for over 5 years, with <strong>over 2,000 training sessions in the last two years</strong>.
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/AmMjGSMS-zs" frameborder="0" allowfullscreen></iframe>
    </div>
    <div class="paragraph">
        It started as a PC + Oculus Rift project (Quest 2 didn't exist yet, and earlier Quest hardware wasn't good enough). Later I ported the whole thing to standalone Quest 2+. Both targets now ship from the same codebase: PC (with Rift or Quest 2+ via Link) and Quest 2+ standalone.
    </div>
    <div class="paragraph">
        The bulk of the engineering was the interaction layer. Hand-tracking gestures map to grabbing equipment, with the gesture input blended into procedural animations so the grip looks and feels natural rather than mechanical. Catheter tubing has its own physics-driven cable system. Nurses operate an ultrasound device to locate the vein, do the needle puncture (<em>puncionamento com agulha</em>), and assemble the catheter kit piece by piece. Wherever I could, components are interactable rather than scripted.
    </div>
    <div class="paragraph">
        Above the interactions, there's the <strong>objective system</strong>, a node-based procedure builder I designed so a new simulation or evaluation is assembled without code. Virtua now has five procedures of 40 to 70 steps each, and an intern built two of them. A configurable scoring pass grades each action in real time, and every procedure is networked by default, so an instructor and trainees share the same scene.
    </div>
    <div class="paragraph">
        One of the five is an evaluation with a short set of questions. The instructor shows ultrasound-guided vein access at the neck, arm, wrist, and leg, each in short-axis and long-axis views, and the trainee answers. The needle simulation exists only for the neck, with both an artery (the patient bleeds out) and a normal vein (the correct procedure).
    </div>
    <div class="paragraph">
        In 2024, the client needed the online multiplayer to also work locally without internet, and Photon's licensing costs for industry use were prohibitive. I <strong>migrated the entire networking layer from Photon to FishNet</strong>. To minimize risk in a large production codebase, I built a <strong>compatibility layer on top of FishNet</strong> that preserved the same architecture and API surface (SyncVars, RPCs, and ownership semantics all behaved identically to the original Photon implementation). This required <strong>Roslyn source generators</strong> for RPC method scaffolding and <strong>IL post-processing</strong> (Mono.Cecil) to rewrite RPC call sites at compile time.
    </div>
    <div class="paragraph">
        Key contributions:
        <ul>
            <li>Interaction layer: gesture grabs with animation blending, catheter cable physics, ultrasound, needle puncture, component-level catheter assembly</li>
            <li>PC + Rift to Quest 2+ standalone port, single codebase for both targets</li>
            <li>Node-based authoring system for procedures and evaluations without code: five procedures of 40 to 70 steps each, two of them built by an intern, all networked by default</li>
            <li>Ultrasound-guided access evaluation at four sites in two views, with needle simulation for artery and vein at the neck</li>
            <li>Configurable scoring evaluator</li>
            <li>Runtime content loading via <strong>Asset Bundles</strong></li>
            <li>Photon to FishNet networking migration with a custom RPC-weaving compat layer (Roslyn codegen + Mono.Cecil IL post-processing)</li>
            <li>LMS integration for trainee progress and certification</li>
        </ul>
    </div>
    `, "#FB8C00", "formulaxr", ["serious-games", "vr"]),

    new ProjectData("edu-portal", "FormulaXR - Educational Portal", "img/projects/edu-portal.png", `
    <div class="paragraph">
        A web platform hosting <strong>60+ interactive science mini-games</strong>, most of them built in Unity. Students drag atoms to build molecules, walk through a virtual ecosystem to learn about food chains, explore an interactive periodic table, balance forces in physics puzzles, and so on. Each one is a small playable title with its own mechanics, scoring, and objectives.
    </div>
    <div class="paragraph">
        I architected the <strong>shared Unity SDK</strong> that the 50+ Unity titles are built on: interaction primitives, scoring, objective tracking, and a WebGL communication bridge that talks back to the portal in real time.
    </div>
    <div class="paragraph">
        I also built the web portal itself, full-stack, in ASP.NET Core and Blazor WebAssembly (student accounts, progress tracking, scoring, reporting).
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/iDR3EGaFzZI" frameborder="0" allowfullscreen></iframe>
    </div>
    <div class="paragraph">
        Key contributions:
        <ul>
            <li>Shared Unity SDK powering the 50+ Unity mini-games (interaction primitives, scoring, objectives)</li>
            <li>WebGL-to-portal communication bridge for student progress and scoring</li>
            <li>Full-stack web portal (ASP.NET Core + Blazor WebAssembly)</li>
            <li>Student tracking, scoring, and reporting</li>
        </ul>
    </div>
    <div class="paragraph">
        <div class="notice">
            Visit <a href="https://formulaxr.com" target="_blank">formulaxr.com</a>
        </div>
    </div>
    <div class="paragraph center">
        <img class="pc-screenshot" src="img/projects/edu-portal-home.png" alt="Portal homepage" />
        <img class="pc-screenshot" src="img/projects/edu-portal-simulation.png" alt="Interactive periodic table simulation" />
    </div>
    `, "#29B6F6", "formulaxr", ["serious-games"]),

    new ProjectData("ortovr", "OrtoVR - Surgical Training", "img/projects/ortovr.png",
    `
    <div class="paragraph">
        <strong>OrtoVR</strong> is a surgical VR training title built for <strong>Ortos&iacute;ntese</strong>, a Brazilian orthopedic company. Surgeons put on a headset and practice placing implants and handling surgical instruments in VR before entering the operating room. Featured as a key demonstration at a major healthcare fair in 2018.
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/yU6mq5-gpb8?start=302" frameborder="0" allowfullscreen></iframe>
    </div>
    <div class="paragraph">
        Key contributions:
        <ul>
            <li>Set up the full project structure</li>
            <li>Step-by-step <strong>objective system</strong> for guided procedure progression, including a 67-step procedure</li>
            <li>Refined a custom VR interaction layer for precise instrument handling</li>
            <li>Real-time multiplayer for collaborative training sessions</li>
        </ul>
    </div>
    `, "#EC407A", "formulaxr", ["serious-games", "vr"]),

    new ProjectData("vr-surgery", "VR Surgery - Hospital BP", "img/projects/vr-surgery.png",
    `
    <div class="paragraph">
        A <strong>proof-of-concept</strong> for <strong>Hospital Benefic&ecirc;ncia Portuguesa de S&atilde;o Paulo (BP)</strong>, among the world's best hospitals for seven consecutive years. The surgeon wears a VR headset and drives a real robotic arm through simulated procedures. Hand movements in VR, real arm in the room, in sync.
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/xIl3_2Z3_X0" frameborder="0" allowfullscreen></iframe>
    </div>
    <div class="paragraph">
        Most of my time went into the <strong>surgical interaction system</strong>: scalpel incisions through skin, scissors cutting vessels, suturing, fat removal with forceps and a spatula together. Each tool has its own physical behavior, with the goal that procedures feel like the real thing instead of triggered animations.
    </div>
    <div class="paragraph">
        The robotic arm also works as a force feedback device, which is a different thing from rumble. It restricts the surgeon's movement where the virtual tool meets tissue, so the scalpel resists as it cuts through skin, scissors bite on a vessel wall, and a pinch lifts fat away. I wrote the peripheral I/O that drives it.
    </div>
    <div class="paragraph">
        Key contributions:
        <ul>
            <li>Surgical interaction system: scalpel incisions, scissor cutting on vessels, suturing, forceps + spatula fat removal</li>
            <li>Force feedback through the robotic arm, which restricts the surgeon's movement at contact</li>
            <li>Hardware drivers for the robotic arm (custom peripheral I/O)</li>
            <li>VR interaction layer and UI</li>
            <li>Refactored and stabilized the existing codebase</li>
        </ul>
    </div>
    `, "#5C6BC0", "formulaxr", ["serious-games", "vr"]),

    new ProjectData("forklift-vr", "Forklift Safety Training", "img/projects/forklift-vr.png", `
    <div class="paragraph">
        A <strong>VR forklift simulator</strong> for <strong>Nestl&eacute;</strong>. Before driving a real forklift in the warehouse, operators put on a Quest headset and go through the safety steps: pre-operation inspection, environment awareness, load handling. Each step is scored in real time and the result is reported back.
    </div>
    <div class="paragraph">
        Built in Unity for standalone Meta Quest. The simulator was deployed for a six-month trial. I had flagged motion sickness as a risk beforehand, and it became the main limit, so I quoted a three-monitor version. The client did not continue.
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/rGpxLB2ws8I" frameborder="0" allowfullscreen></iframe>
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/ZTwBLDFgxso" frameborder="0" allowfullscreen></iframe>
    </div>
    <div class="paragraph">
        Key contributions:
        <ul>
            <li>Full development of the simulator</li>
            <li>Real-time scoring with step-based progression</li>
            <li><strong>Vehicle physics and controls</strong> tuned for VR</li>
            <li>Performance optimization for standalone Quest (stereo, 72 Hz)</li>
        </ul>
    </div>
    `, "#EF5350", "formulaxr", ["serious-games", "vr"]),

    new ProjectData("painter-sim", "Industrial Painter Simulator", "img/projects/painter-sim.png", `
    <div class="paragraph">
        Multi-user simulation of an industrial painting machine, built for a major multinational corporation. One person wears a VR headset and walks around the virtual machine while teammates on their laptops observe and annotate from a desktop client. <strong>Both share the same world in real time</strong>, with different inputs and views per role.
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/jnUIvtkdHCs" frameborder="0" allowfullscreen></iframe>
    </div>
    <div class="paragraph">
        Key contributions:
        <ul>
            <li>Full development of the codebase</li>
            <li>Real-time multiplayer with simultaneous VR and desktop participants</li>
            <li>Layer system and animation logic for the painting machine</li>
            <li>Cross-platform client (VR headset + non-VR desktop) from a single codebase</li>
        </ul>
    </div>
    `, "#78909C", "formulaxr", ["serious-games", "vr"]),

    new ProjectData("vr-classroom", "Virtual Classroom", "img/projects/vr-classroom.png",
    `
    <div class="paragraph">
        A <strong>multiplayer VR classroom</strong>, built in collaboration with the City Hall of S&atilde;o Jos&eacute; dos Campos, Brazil. Students wearing VR headsets join the same virtual classroom from different locations and attend interactive lessons together. This is also where my reusable <strong>VR interaction layer and networking code</strong> first came together. Most of my later VR projects still build on what I wrote here.
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/8lTZSyZExXM" frameborder="0" allowfullscreen></iframe>
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/SSbevc_ezho" frameborder="0" allowfullscreen></iframe>
    </div>
    <div class="paragraph">
        Key contributions:
        <ul>
            <li>Extensible VR interaction layer (teleportation, object manipulation) reused across later projects</li>
            <li>Modular multiplayer layer designed for easy extension</li>
            <li>Performance work for smooth multiplayer sessions</li>
        </ul>
    </div>
    `, "#42A5F5", "formulaxr", ["serious-games", "vr"]),

    new ProjectData("hyundai-vr", "Hyundai - VR Archery", "img/projects/hyundai-vr.png", `
    <div class="paragraph">
        A <strong>VR archery game</strong> developed for <strong>Hyundai</strong> as a brand activation at the 2017 Auto Show (<em>Feira do Autom&oacute;vel</em>). Visitors at the Hyundai booth put on a headset, grabbed a controller like a bow, and competed in an archery challenge.
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/KMV1TCsMhX8" frameborder="0" allowfullscreen></iframe>
    </div>
    <div class="paragraph">
        Key contributions:
        <ul>
            <li>Full development of the VR game</li>
            <li>Archery mechanics and physics for VR controllers</li>
            <li>Event-ready experience optimized for booth demonstrations</li>
        </ul>
    </div>
    `, "#3F51B5", "formulaxr", ["games", "event-activations", "vr"]),

    new ProjectData("vr-racing", "VR Racing Game", "img/projects/vr-racing.png",
    `
    <div class="paragraph">
        A <strong>VR racing game</strong> developed for a Brazilian motorized wheelchair company and showcased at <strong>Rock in Rio 2017</strong>, one of the world's largest music festivals. Festival-goers sat in the wheelchair, grabbed the handlebars, and raced each other on a virtual track.
    </div>
    <div class="paragraph">
        A key challenge was integrating an <strong>HTC Vive Tracker</strong> mounted on the wheelchair's handlebar. The tracker captures the real steering input and maps it directly to the in-game vehicle controls.
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/1reoW8gjKrw" frameborder="0" allowfullscreen></iframe>
    </div>
    <div class="paragraph">
        Key contributions:
        <ul>
            <li>Integrated HTC Vive Tracker for real-world handlebar-to-game steering input</li>
            <li>Customized Unity's vehicle physics system for wheelchair-based controls</li>
            <li>Implemented multiplayer racing for competitive on-site play</li>
        </ul>
    </div>
    `, "#424242", "formulaxr", ["games", "event-activations", "vr"]),

    new ProjectData("sanja-runner", "Sanja Runner", "img/projects/sanja-runner.png", `
    <div class="paragraph">
        <strong>Sanja Runner</strong> is an educational 2D adventure game developed in 2016 for the city of S&atilde;o Jos&eacute; dos Campos, Brazil. Players run through five levels based on real tourist landmarks (Parque do Banhado, Parque Vicentina Aranha, and others), collecting scrolls with educational content and answering quizzes along the way.
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/inHtwPzmylE" frameborder="0" allowfullscreen></iframe>
    </div>
    <div class="paragraph">
        Built for PC and Android, the game features a content management system that lets educators customize scrolls and quiz questions through a web interface, making it adaptable for different subjects and learning goals.
    </div>
    `, "#7CB342", "formulaxr", ["games"]),

    new ProjectData("wind-tunnel", "Wind Tunnel - Samsung Creative Startups", "img/projects/wind-tunnel.jpg", `
    <div class="paragraph">
        Multi-user wind tunnel simulation developed during FormulaXR's participation in the <strong><a href="https://news.samsung.com/br/samsung-creative-startups-divulga-12-startups-selecionadas-para-a-4a-rodada-do-programa-de-aceleracao" target="_blank">Samsung Creative Startups</a></strong> investment program (2018). Fluid dynamics created in <strong>Maya</strong>, visualized as a <strong>multi-user experience in Unity</strong>. I developed the <strong>Unity visualization and networking</strong>.
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/JdSvBvm7Fxc" frameborder="0" allowfullscreen></iframe>
    </div>
    `, "#7986CB", "formulaxr", ["serious-games"]),

    new ProjectData("dupont-vr", "DuPont - VR Product Tour", "img/projects/dupont-vr.png", `
    <div class="paragraph">
        VR city tour shown at <strong>DuPont</strong>'s internal event <strong>"Next Generation DuPont"</strong> (3 days, <strong>800+ attendees</strong>). Users explored a virtual city highlighting DuPont products in everyday environments. Built on the <strong>Oculus DK2</strong>; I did <strong>full development</strong> of the VR experience.
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/KZkpDJGheIk" frameborder="0" allowfullscreen></iframe>
    </div>
    <div class="paragraph center">
        <img class="pc-screenshot" src="img/projects/dupont-event-1.jpg" alt="DuPont Next Generation event" />
        <img class="pc-screenshot" src="img/projects/dupont-event-2.jpg" alt="Attendees experiencing the VR tour" />
        <img class="pc-screenshot" src="img/projects/dupont-event-3.jpg" alt="Event booth" />
    </div>
    `, "#E57373", "formulaxr", ["event-activations", "vr"]),

    new ProjectData("ortosintese-fh", "Ortosíntese - Feira Hospitalar 2019", "img/projects/ortosintese-fh.jpg", `
    <div class="paragraph">
        <strong>Unreal Engine</strong> VR product showcase for <strong><a href="https://ortosintese.com.br/" target="_blank">Ortos&iacute;ntese</a></strong>, presented at <strong>Feira Hospitalar 2019</strong> (largest healthcare trade fair in Latin America). I developed the <strong>VR navigation and basic interactivity</strong> for exploring their orthopedic equipment catalog.
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/P80BQk8bIBU" frameborder="0" allowfullscreen></iframe>
    </div>
    `, "#EC407A", "formulaxr", ["event-activations", "vr"]),

    new ProjectData("climatempo-vr", "Climatempo - VR Experience", "img/projects/climatempo-vr.png", `
    <div class="paragraph">
        VR experience presented at the <strong>RM Vale 2016</strong> event for <strong><a href="https://www.climatempo.com.br/" target="_blank">Climatempo</a></strong> and <strong>Kersys</strong>. I did <strong>full development</strong> of the application. Interactive scenes let users explore weather and environmental data in VR.
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/-JgiY85GKkc" frameborder="0" allowfullscreen></iframe>
    </div>
    `, "#FF8F00", "formulaxr", ["event-activations", "vr"]),

    new ProjectData("casa-fitness", "Casa do Fitness - VR Showroom", "img/projects/casa-fitness.jpg", `
    <div class="paragraph">
        <strong>Unreal Engine</strong> VR showroom for <strong><a href="https://www.casadofitness.com.br/" target="_blank">Casa do Fitness</a></strong>, a Brazilian fitness equipment retailer. I developed the <strong>VR navigation and interaction system</strong> within the environment built by the art team.
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/cjIBSUD5deM" frameborder="0" allowfullscreen></iframe>
    </div>
    `, "#FF8A65", "formulaxr", ["event-activations", "vr"]),

    // ===== Freelance =====
    new ProjectData("vr-basketball", "VR Basketball", "img/projects/vr-basketball.png",
    `
    <div class="paragraph">
        <strong>VR Basketball</strong> is a freelance VR title built in <strong>Unreal Engine 5 with C++</strong> for standalone <strong>Meta Quest 2</strong>. You reach out, grab the ball, throw it, and the ball curves and spins realistically. <strong>All the gameplay logic is in C++</strong>, with Blueprint over it for the visual and designer-tunable layers, which is where Blueprint earns its keep.
    </div>
    <div class="paragraph">
        I wrote the VR interaction layer myself in C++: hand presence, grab mechanics, physics-driven ball throwing, scoring loop. Everything runs natively on the Quest 2 with no PC required, on a standalone-mobile frame budget.
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/rLnq_LxKHoE" frameborder="0" allowfullscreen></iframe>
    </div>
    <div class="paragraph">
        Key highlights:
        <ul>
            <li>C++ VR interaction layer (grab, throw, hand presence), with Blueprint over it for the visual and tunable layers</li>
            <li>Physics-driven ball handling with realistic trajectory and spin</li>
            <li>Scoring loop with multiple court positions</li>
            <li>Optimized for standalone Meta Quest 2 (stereo, 72&ndash;90 Hz)</li>
        </ul>
    </div>
    `, "#FF7043", "freelance", ["games", "vr"]),

    new ProjectData("truco-animado", "Truco Animado - Multiplayer Rewrite", "img/projects/truco-animado.png",
    `
    <div class="paragraph">
        <strong>Truco Animado</strong> is an established mobile card game on Google Play (Truco is one of Brazil's most popular card games). I was brought in as a freelancer to <strong>architect and ship the real-time multiplayer rewrite</strong> on top of an existing single-player codebase that already had a large player base.
    </div>
    <div class="paragraph">
        The title has <strong>1M+ downloads and 24.2K ratings</strong> on Google Play and is still being updated in 2026. Those numbers belong to the game and predate my work. My scope was the multiplayer, not the game design or content. Making an existing single-player game work online meant recreating many of its features so they ran properly in multiplayer, on top of the networking layer, matchmaking, and lobby I built. After the multiplayer milestone shipped I handed the code back.
    </div>
    <div class="paragraph">
        Key contributions:
        <ul>
            <li>Architected and implemented the real-time multiplayer networking layer</li>
            <li>Developed the matchmaking and lobby system</li>
            <li>Recreated many of the game's features so they work properly in multiplayer</li>
        </ul>
    </div>
    <div class="paragraph">
        <div class="notice">
            Available on <a href="https://play.google.com/store/apps/details?id=br.com.delotech.trucoindie" target="_blank">Google Play</a>
        </div>
    </div>
    `, "#66BB6A", "freelance", ["games"]),

    new ProjectData("rick-dangerous", "Rick Dangerous - Remake", "img/projects/rick-dangerous.png",
    `
    <div class="paragraph">
        A remake of the classic arcade game <strong>Rick Dangerous</strong>, a freelance project where I led a small team (an artist, two junior developers, and later two junior level designers) and taught them while we built it. It's a remake with new, higher-resolution art and was never officially released.
    </div>
    <div class="paragraph">
        I began on the tile map, then moved to the code, where the juniors were struggling. I set up Corgi Engine for the character controller, created the enemies and showed the team how to build more, and created the obstacles and traps and taught them how to make new ones. I also taught the artist to make sprites with the right sizes and margins, and taught the level designers to build the tile map. One of the juniors from that team started with almost no Unity experience and went on to develop VR and AR solutions professionally.
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/MZVmhxrgpsE" frameborder="0" allowfullscreen></iframe>
    </div>
    <div class="paragraph">
        Key contributions:
        <ul>
            <li>Led a team of an artist and two junior developers, and taught two junior level designers</li>
            <li>Set up Corgi Engine for the character controller</li>
            <li>Created the enemies, obstacles, and traps, and taught the team to create new ones</li>
            <li>Taught the artist to make sprites with the right sizes and margins</li>
            <li>Created reusable prefabs and tilemaps for level designers</li>
        </ul>
    </div>
    <div class="paragraph">
        <div class="notice">
            Playable in the browser on <a href="https://luizcarlosfx.itch.io/rick-dangerous" target="_blank">itch.io</a>
        </div>
    </div>
    `, "#8D6E63", "freelance", ["games"]),

    new ProjectData("fox-trot", "Fox Trot", "img/projects/fox-trot.png",
    `
    <div class="paragraph">
        <strong>Fox Trot</strong> is an endless runner for mobile. I was brought in as a freelancer to develop the core gameplay systems, including the obstacle, collectible, and daily reward mechanics. The client published it on the mobile stores and later removed it. The WebGL build is a demo only.
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/s0U8bGTIkNo" frameborder="0" allowfullscreen></iframe>
    </div>
    <div class="paragraph">
        Key contributions:
        <ul>
            <li>Developed core gameplay systems (obstacles, collectibles, daily rewards)</li>
            <li>Prepared the WebGL demo build</li>
        </ul>
    </div>
    <div class="paragraph">
        <div class="notice">
            A WebGL demo is playable in the browser on <a href="https://luizcarlosfx.itch.io/foxtrot" target="_blank">itch.io</a>
        </div>
    </div>
    `, "#FFA000", "freelance", ["games"]),

    new ProjectData("infinite-clockwise", "Infinite Clockwise", "img/projects/infinite-clockwise.jpg",
    `
    <div class="paragraph">
        <strong>Infinite Clockwise</strong> is a casual arcade game for Android and iOS (2022). You tap to dodge moving obstacles on an ever-changing course, with scoring, progression, and unlockable achievements. I was brought in as a freelancer and built the full gameplay. The client published it on Google Play and the App Store and handled the ads and services integration. The owner has since removed it from the stores.
    </div>
    <div class="paragraph">
        Key contributions:
        <ul>
            <li>Built the full gameplay, including the tap-to-dodge obstacle course, scoring, progression, and unlockable achievements</li>
            <li>One Unity project shipped to Android and iOS, published by the client</li>
        </ul>
    </div>
    <div class="paragraph">
        <div class="notice">
            A listing is still visible on <a href="https://www.taptap.io/app/259250" target="_blank">TapTap</a>
        </div>
    </div>
    `, "#FF7043", "freelance", ["games"]),

    // ===== Personal =====
    new ProjectData("pivot-editor", "Pivot Editor", "img/projects/pivot-editor.png",
    `
    <div class="paragraph">
        <strong>Pivot Editor</strong> is a Unity Editor extension published on the Unity Asset Store that allows developers to visually edit mesh pivot points directly in the editor using handle-based controls.
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/r5iluHpuyZE" frameborder="0" allowfullscreen></iframe>
    </div>
    <div class="paragraph">
        It sold on the Asset Store from <strong>December 2015 to 2026</strong>, reaching <strong>600+ developers</strong> at a <strong>4.8 star rating across 27 reviews</strong>. Sales held steady for eight straight years with no marketing and no feature work, which is the part worth pointing at: a small, tightly scoped tool that kept solving the same real problem while Unity's editor API changed underneath it.
    </div>
    <div class="paragraph">
        Keeping a commercial Editor tool working through a decade of Unity releases is a different discipline from shipping a project. Unity's Editor API changed underneath it, and paying users were spread across many Unity versions, so keeping the tool small and tightly scoped mattered far more than adding features.
    </div>
    <div class="paragraph">
        Key contributions:
        <ul>
            <li>Designed and built the custom Editor Window</li>
            <li>Implemented pivot manipulation logic with visual handles</li>
            <li>Published, supported and maintained the asset across <strong>10+ years of Unity versions</strong></li>
        </ul>
    </div>
    <div class="paragraph">
        <div class="notice">
            Available on the <a href="http://u3d.as/kT1" target="_blank">Unity Asset Store</a>
        </div>
    </div>
    `, "#757575", "personal", ["tools-sdks"]),

    new ProjectData("purge-all-heroes", "Purge All Heroes", "img/projects/purge-all-heroes.png",
    `
    <div class="paragraph">
        <strong>Purge All Heroes</strong> is a tactical turn-based RPG with grid-based combat, built in <strong>Unreal Engine</strong> on the Angelscript branch. I joined an online community-collaborative team mid-development and rebuilt the combat system from scratch.
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/TG9raAACyjU" frameborder="0" allowfullscreen></iframe>
    </div>
    <div class="paragraph">
        The team had a Blueprint-only combat prototype that wasn't holding up. I moved the project to <strong>Hazelight's Unreal-Angelscript branch</strong> and rebuilt the combat system from scratch on top of Unreal's <strong>Gameplay Ability System (GAS)</strong>.
    </div>
    <div class="paragraph">
        The technically interesting piece: GAS is built around time-based gameplay effects (durations in seconds, periodic ticks at fixed intervals). For a turn-based game, that whole model needs to translate to discrete turn ticks, with every effect duration, periodic application, and stacking rule re-expressed in turn counts instead of timers.
    </div>
    <div class="paragraph">
        A second core decision was the architecture for designer extensibility. Most game logic fires <strong>events</strong> at the meaningful trigger points (ability cast, effect applied, turn started, hit landed, and so on), and the designer hooks into those events from Blueprint. The Angelscript core stays clean; the design side gets full extensibility without going through engineering for every tweak.
    </div>
    <div class="paragraph">
        Key contributions:
        <ul>
            <li>Rebuilt the combat system from scratch in Angelscript on top of Unreal's Gameplay Ability System (GAS)</li>
            <li><strong>Adapted GAS for turn-based gameplay</strong>: effect durations, periodic ticks, and stacks driven by turn counts instead of seconds</li>
            <li>Grid-based movement for player and enemies</li>
            <li>Enemy AI built on Unreal's <strong>Behavior Tree</strong>, deciding movement, targeting, and triggering of complex abilities each turn</li>
            <li><strong>Event-driven architecture</strong>: trigger points exposed throughout the combat flow so the designer can hook custom logic in Blueprint without touching the Angelscript core</li>
            <li>Designer-friendly Blueprint Interfaces over the Angelscript core</li>
            <li>Mentored the game designer on GAS during development</li>
        </ul>
    </div>
    <div class="paragraph">
        The project is currently on hold; revisiting it in the future is on the roadmap.
    </div>
    `, "#6A1B9A", "personal", ["games"]),

    new ProjectData("mercenary-rush", "Mercenary Rush", "img/projects/mercenary-rush.png",
    `
    <div class="paragraph">
        <strong>Mercenary Rush</strong> is a 2D multiplayer platformer and shooter I built in 2014 as a capstone project during my Computer Science degree, with the whole character controller written from scratch on a raw Rigidbody2D. The idea was to run through platformer levels in multiplayer while shooting and using powers on the other players.
    </div>
    <div class="paragraph">
        The controller handles short and long jumps, wall slide and wall jump, ledge hang and climb up, wall and ladder climbing, buoyancy in water, and two kinds of rope. One is a rope you climb and set swinging, and the other is a horizontal rope you grab and move along, like in Donkey Kong Country 2. The levels also have portals and laser beams. I got two weapons working, with an aim system where inverse kinematics blends into the Mecanim animations.
    </div>
    <div class="paragraph">
        The transform interpolation I wrote for the networking here is still used in XRProj and Virtua today.
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/6Mq_5XopKMI" frameborder="0" allowfullscreen></iframe>
    </div>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/LHyG7JLcBWw" frameborder="0" allowfullscreen></iframe>
    </div>
    <div class="paragraph">
        Key contributions:
        <ul>
            <li>Character controller from scratch on Rigidbody2D, covering jumps, wall slide and wall jump, ledge hang and climb up, ladders, wall climbing, swinging ropes, horizontal ropes and buoyancy</li>
            <li>Aim system with inverse kinematics blended into the animations</li>
            <li>Level mechanics including portals and laser beams</li>
            <li>Real-time multiplayer with transform interpolation, the approach XRProj and Virtua still use</li>
            <li>Custom editor tools (inspectors, windows, menus) for rapid iteration</li>
        </ul>
    </div>
    `, "#AB47BC", "personal", ["games"]),

    new ProjectData("dk-clone", "Donkey Kong 2 - Clone", "img/projects/dk-clone.png",
    `
    <div class="paragraph">
        A hobby remake of <strong>Donkey Kong</strong>, built in Unity. The part worth showing is the sprite pipeline. With no artist on the team, I took ripped spritesheets from community resources and had to turn them into aligned, working animations. I built an in-engine tool that lets me move an animation frame pixel by pixel while it plays, until it looks right. The character controller is unfinished, because preparing the sprites took more work than I wanted to put into a side project.
    </div>
    <div class="paragraph">
        <div class="notice">
            Watch the development playlist on <a href="https://www.youtube.com/playlist?list=PLedBjvIQnDOFqD_1fc-kaYi6rJwkAHIXX" target="_blank">YouTube</a>
        </div>
    </div>
    <div class="paragraph">
        Key contributions:
        <ul>
            <li>In-engine tool to align animation frames pixel by pixel while the animation plays</li>
            <li>Sprite cutting and positioning from raw spritesheets</li>
            <li>2D character controller with dual playable characters (unfinished)</li>
            <li>Collectible, damage, and NPC systems</li>
        </ul>
    </div>
    `, "#EF5350", "personal", ["games"]),
];

// Order definitions for each tab. Each tab knows which projects appear and in what order.
// "all" is a single flat list. The first ten are the lead-in; after that it alternates
// a production piece with a game or smaller project so the page keeps its rhythm.

export const allOrder: string[] = [
    "virtua",
    "pivot-editor",
    "vr-classroom",
    "ortovr",
    "threejs-editor",
    "edu-portal",
    "truco-animado",
    "vr-basketball",
    "xrproj",
    "xrtracker",
    "purge-all-heroes",
    "vr-surgery",
    "vr-racing",
    "hyundai-vr",
    "rick-dangerous",
    "painter-sim",
    "forklift-vr",
    "dupont-vr",
    "mercenary-rush",
    "ortosintese-fh",
    "dk-clone",
    "infinite-clockwise",
    "fox-trot",
    "wind-tunnel",
    "casa-fitness",
    "climatempo-vr",
    "sanja-runner",
];


// Tabs follow `allOrder` by default. A tab listed here uses its own ranking instead,
// for when a tab reads differently from the overall list.
export const tabOverrides: Record<string, string[]> = {
    games: [
        "truco-animado",
        "purge-all-heroes",
        "vr-basketball",
        "mercenary-rush",
        "rick-dangerous",
        "vr-racing",
        "hyundai-vr",
        "infinite-clockwise",
        "fox-trot",
        "sanja-runner",
        "dk-clone",
    ],
};

// Short or older pieces. The All and Games tabs fold these behind a "More work" button
// so the first screen shows the projects with the strongest evidence. Every other tab shows them in full.
export const moreWorkIds: string[] = [
    "dk-clone",
    "sanja-runner",
    "wind-tunnel",
    "dupont-vr",
    "ortosintese-fh",
    "climatempo-vr",
    "casa-fitness",
];

export const moreWorkTabs: string[] = ["all", "games"];
